/**
 * Subida de VARIOS archivos elegidos a la vez, para los antecedentes del
 * expediente y para el portal del cliente.
 *
 * DOS DECISIONES que este archivo fija, porque las dos pantallas tienen que
 * coincidir:
 *
 * - **Una petición POR archivo y EN SECUENCIA.** No un arreglo en un solo POST
 *   (`post_max_size` corta en 48 MB y cada archivo puede pesar 32) ni en
 *   paralelo: cada respuesta devuelve la ficha COMPLETA y se escribe en la
 *   caché, y con dos en vuelo una respuesta vieja podía pisar a una nueva.
 * - **Un archivo rechazado NO frena el lote.** Al revés que las fotos de una
 *   fila, que se rechazan juntas: acá cada archivo es un documento
 *   independiente, y perder diez subidas buenas por un duplicado sería castigar
 *   a quien ya hizo el trabajo.
 */

/** Un archivo que no entró, con el motivo, para decirlo POR NOMBRE. */
export interface FalloDeArchivo {
  nombre: string;
  motivo: string;
}

/**
 * Aparta lo que el NAVEGADOR ya puede rechazar (tipo y peso) sin gastar una
 * subida. Conserva el orden en que se eligieron.
 */
export function separarPorValidez(
  archivos: File[],
  motivoDeRechazo: (archivo: File) => string | null,
): { validos: File[]; rechazados: FalloDeArchivo[] } {
  const validos: File[] = [];
  const rechazados: FalloDeArchivo[] = [];

  for (const archivo of archivos) {
    const motivo = motivoDeRechazo(archivo);
    if (motivo === null) validos.push(archivo);
    else rechazados.push({ nombre: archivo.name, motivo });
  }

  return { validos, rechazados };
}

/**
 * Sube los archivos de a uno, esperando cada respuesta antes de mandar el
 * siguiente. Nunca lanza: lo que el servidor rechaza vuelve en `fallos`, con el
 * motivo que devuelva `motivoDelError`, y el lote sigue.
 */
export async function subirEnSecuencia(
  archivos: File[],
  subir: (archivo: File) => Promise<unknown>,
  motivoDelError: (error: unknown) => string,
): Promise<{ subidos: File[]; fallos: FalloDeArchivo[] }> {
  const subidos: File[] = [];
  const fallos: FalloDeArchivo[] = [];

  for (const archivo of archivos) {
    try {
      await subir(archivo);
      subidos.push(archivo);
    } catch (error) {
      fallos.push({ nombre: archivo.name, motivo: motivoDelError(error) });
    }
  }

  return { subidos, fallos };
}

/** Una línea por archivo, "nombre: motivo". Vacío si no hubo fallos. */
export function textoDeFallos(fallos: FalloDeArchivo[]): string {
  return fallos.map((fallo) => `${fallo.nombre}: ${fallo.motivo}`).join("\n");
}
